import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Central notifications state manager. Consumes the global "crm:realtime" event
 * (dispatched by useAgentRealtime for both admin + agent layouts) so newly
 * arrived notifications flow in without a manual refetch.
 *
 * Returns: { items, loading, freshIds, markRead, markAllRead, remove,
 *            clearRead, clearAll, refresh }
 *
 * `freshIds` is the set of notification ids that just arrived via WS — used
 * by NotificationCard to add a slide-in animation for one render cycle.
 */
export function useNotifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [freshIds, setFreshIds] = useState(new Set());
  const clearTimer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Merge real-time pushes into the list at position 0.
  useEffect(() => {
    const onRealtime = (evt) => {
      const msg = evt && evt.detail;
      if (!msg) return;
      if (msg.type === "notification" && msg.notification) {
        const n = msg.notification;
        setItems((prev) => {
          if (prev.some((x) => x.id === n.id)) return prev;
          return [n, ...prev].slice(0, 200);
        });
        setFreshIds((prev) => {
          const next = new Set(prev);
          next.add(n.id);
          return next;
        });
        // Clear "fresh" flag after the slide-in animation completes.
        if (clearTimer.current) clearTimeout(clearTimer.current);
        clearTimer.current = setTimeout(() => setFreshIds(new Set()), 1200);
      }
      if (msg.type === "notifications_cleared") {
        setItems([]);
      }
    };
    window.addEventListener("crm:realtime", onRealtime);
    // Also honour the legacy in-app trigger from other pages.
    const onLegacy = () => refresh();
    window.addEventListener("crm:notifs-changed", onLegacy);
    return () => {
      window.removeEventListener("crm:realtime", onRealtime);
      window.removeEventListener("crm:notifs-changed", onLegacy);
      if (clearTimer.current) clearTimeout(clearTimer.current);
    };
  }, [refresh]);

  const _emitChanged = () => {
    try { window.dispatchEvent(new CustomEvent("crm:notifs-changed")); } catch (_) {}
  };

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try { await api.post(`/notifications/${id}/read`); _emitChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try { await api.post("/notifications/read-all"); _emitChanged(); toast.success("All marked as read"); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const remove = useCallback(async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try { await api.delete(`/notifications/${id}`); _emitChanged(); }
    catch (e) { toast.error(formatApiError(e)); refresh(); }
  }, [refresh]);

  const clearRead = useCallback(async () => {
    setItems((prev) => prev.filter((n) => !n.read));
    try { await api.post("/notifications/clear-read"); _emitChanged(); toast.success("Cleared read notifications"); }
    catch (e) { toast.error(formatApiError(e)); refresh(); }
  }, [refresh]);

  const clearAll = useCallback(async () => {
    setItems([]);
    try { await api.delete("/notifications/all"); _emitChanged(); toast.success("All cleared"); }
    catch (e) { toast.error(formatApiError(e)); refresh(); }
  }, [refresh]);

  return { items, loading, freshIds, markRead, markAllRead, remove, clearRead, clearAll, refresh };
}
